import React from "react";
import ViewDocument from "../../ui/ViewDocument";

const CareNepal = () => {
  return (
    <div
      id="care-economy-nepal"
      className="space-y-4 text-softBlack leading-loose text-base font-normal !mt-14 scroll-mt-24"
    >
      <h2 className="text-xl font-bold !mt-10 mb-4">
        The care economy in Nepal
      </h2>

      <h3 className="text-base font-bold mt-4 mb-3">
        Current status of the care economy
      </h3>
      <p>
        A joint program of the ILO and UN Women organized a{" "}
        <a
          href="https://www.ilo.org/resource/news/ilo-un-women-joint-programme-decent-employment-women-care-economy-concludes"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          policy dialogue
        </a>{" "}
        in August 2023 with government representatives, as well as members of
        the private sector and civil society organizations. The joint program
        focused on promoting decent employment for women through inclusive
        growth policies and investments in the care economy, and related
        research. At the end of the program, an analysis of education and
        healthcare coverage in Nepal – conducted by the Institute for Integrated
        Development Studies (IIDS) and the National Planning Commission (NPC) –
        was released. According to this analysis, an investment of 272, 232
        million NPR (approximately USD 19.8 million) could meet Nepal’s care
        coverage gaps in education and healthcare, and this would generate
        1,386,000 jobs, of which 60 percent would be filled by women.
      </p>

      <h3 className="text-base font-semibold italic">
        Female labor force participation
      </h3>
      <p>
        ILO data, cited in a 2023{" "}
        <a
          href="https://asiapacific.unwomen.org/sites/default/files/2024-06/np-c960-policy-brief-20230912.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          policy brief
        </a>{" "}
        by ILO-UN Women and the NPC, Nepal, shows that 85 percent of daily
        unpaid care work is done by women, with women spending more than six
        times the number of hours a day on care work, as compared to men. This
        amounts to spending a total of 29 million hours a day (cumulatively) on
        unpaid care work, compared to 5 million hours spent by men. According to{" "}
        <a
          href="https://nepalitimes.com/opinion/nepals-care-economy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          independent research
        </a>
        , the total value of unpaid care and domestic work is estimated to be
        between 6 and 17 percent of GDP.
      </p>

      <p>
        According to World Bank data in the brief, women’s labor force
        participation rate was only 28.7 percent in 2023 - the{" "}
        <a
          href="https://genderdata.worldbank.org/en/economies/nepal"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          ILO estimates
        </a>{" "}
        it at 27.6 percent in 2024. The brief also cites Nepal’s National Labor
        Force Survey from 2017-18, which states that among persons not active in
        the workforce, 39.7 percent of women indicated that unpaid care work was
        the main reason, whereas only 4.6 percent of men gave the same reason.
      </p>

      <h3 className="text-base font-bold !mt-5">
        Barriers to a strong care economy
      </h3>
      <p>According to the policy brief by the ILO-UN Women and NPC:</p>
      <ul className="list-disc list-outside space-y-2 ml-6">
        <li>
          The Nepal government has not invested in early childcare
          infrastructure or services, due to which mothers and family members
          are forced to provide unpaid care work
        </li>
        <li>
          Care responsibilities have also resulted in school dropouts,
          particularly for girls, who are then unable to enter the labour
          market.
        </li>
      </ul>

      <h3 className="text-base font-bold !mt-5">Care economy initiatives</h3>
      <p>
        The ILO-UN Women program ended with a call to the Government of Nepal to
        include concerns about the care economy in its 16th Periodic Plan, as
        well as labor policies. In January 2024,{" "}
        <a
          href="https://asiapacific.unwomen.org/en/stories/news/2024/02/nepal-estalishes-a-care-working-group"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue underline"
        >
          UN Women reported
        </a>{" "}
        that the NPC had established a ‘Care Working Group’ , comprising
        representatives from the Ministry of Women, Children and Senior
        Citizens, Ministry of Labour, Employment and Social Security, Ministry
        of Health and Population Ministry of Education, Science and Technology,
        as well as the National Statistics Office, National Women’s Commission,
        Federation of Nepalese Chamber of Commerce & Industries, UN Women, ILO,
        and officials from the NPC.
      </p>
      <p>
        The aim of the group is to increase the participation of women in decent
        employment, work towards providing them equal wage access, include them
        in social protection schemes, and create changes in gender relations to
        empower them.
      </p>

      <h3 className="text-base font-bold !mt-5">Expert recommendations</h3>
      <ul className="list-disc list-outside space-y-2 ml-6">
        <li>
          Develop community-level care services to address gaps in enrolment
          rates and retention of students
        </li>
        <li>
          Introduce parental leave, care leave or care allowance, flexible
          working hours, breastfeeding entitlements, and childcare centers at
          the workplace
        </li>
        <li>
          Improve access to childcare institutions, including establishing
          childcare centers at the workplace
        </li>
        <li>
          Gender-responsive budgeting, policy planning and investments to
          consider the needs of women
        </li>
        <li>
          Recording and monitoring data on employment and the status of
          long-term care disaggregated by sex, in order to inform policy
          decisions
        </li>
        <li>Providing social security to vulnerable populations</li>
        <li>
          The government should work closely with the private sector, civil
          society organizations and development partners to mobilize resources
          and begin changing social norms around care
        </li>
      </ul>

      <ViewDocument
        description="This document contains a list of schemes, policies and practices in Nepal, which directly and indirectly benefit caregivers and their dependents"
        documentUrl="https://sapient-dev-bucket-projects.s3.ap-south-1.amazonaws.com/noora-health/uploads/85880701356312871788.Care-Economy---Nepal---Schemes-and-policies-supporting-caregivers.pdf"
        className="!my-8"
      />
    </div>
  );
};

export default CareNepal;
